/**
 * AttendanceDailyView (ATT-DAILY-001)
 * Default Attendance Reports view — ONE row per employee per local
 * attendance date, with quick filter chips, server-side pagination and
 * the Session History drawer. Aggregation happens server-side
 * (GET /api/reports/attendance/daily) using the exact Payroll Summary math.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, ChevronLeft, ChevronRight, History, ArrowUp, ArrowDown, ArrowUpDown, Users, CalendarCheck2, Timer, Layers, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { AnKpiCard, AnAvatar, AnStatusPill, AnLoadingState, AnErrorState, AnEmptyState, AnCard, AnDelta } from '../analytics/ui/AnalyticsUi';
import AttendanceDayDrawer from './AttendanceDayDrawer';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Quick filters double as KPI cards (same summary counts, same toggle behaviour)
const QUICK_FILTERS = [
  { key: 'active_now', label: 'Active Now', countKey: 'active_now', icon: Users, tone: 'green', live: true },
  { key: 'completed_today', label: 'Completed Today', countKey: 'completed_today', dayKey: 'completed', icon: CalendarCheck2, tone: 'blue' },
  { key: 'short_sessions', label: 'Short Sessions', countKey: 'short_sessions', dayKey: 'short_sessions', icon: Timer, tone: 'orange', invert: true },
  { key: 'multiple_sessions', label: 'Multiple Sessions', countKey: 'multiple_sessions', dayKey: 'multiple_sessions', icon: Layers, tone: 'purple', invert: true },
  { key: 'needs_review', label: 'Needs Review', countKey: 'needs_review', dayKey: 'needs_review', icon: ShieldAlert, tone: 'red', invert: true },
];

// Trend = real per-day counts from summary.by_day (same definitions, same filtered range). Comparison = today vs yesterday, only when both days are in range.
const trendFor = (f, days, today) => {
  if (!f.dayKey || days.length < 2) return {};
  const series = days.map(d => d[f.dayKey] || 0);
  const ti = days.findIndex(d => d.date === today);
  if (ti < 1) return { series };
  const cur = days[ti][f.dayKey] || 0, prev = days[ti - 1][f.dayKey] || 0;
  const pct = prev === 0 ? (cur === 0 ? 0 : null) : Math.round(((cur - prev) / prev) * 100);
  return { series, delta: { pct, abs: cur - prev } };
};

const fmtDate = (d) => {
  try {
    // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional compact attendance header
    return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};
const fmtRole = (role) => role ? role.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : '--';

// [label, server sort field or null]
const COLUMNS = [
  ['Employee', 'user_name'],
  ['Role', 'role'],
  ['Date', 'work_date'],
  ['Shift', null],
  ['First Login', 'first_login'],
  ['Last Logout', 'last_logout'],
  ['Sessions', 'session_count'],
  ['Total', 'total_worked_minutes'],
  ['Break', 'break_minutes'],
  ['Net Payable', 'net_payable_minutes'],
  ['Status', null],
  ['', null],
];

// Windowed page numbers with ellipsis: 1 … 4 5 [6] 7 8 … 20
const pageNumbers = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  const lo = Math.max(2, page - 1);
  const hi = Math.min(pages - 1, page + 1);
  if (lo > 2) out.push('…');
  for (let i = lo; i <= hi; i++) out.push(i);
  if (hi < pages - 1) out.push('…');
  out.push(pages);
  return out;
};

export default function AttendanceDailyView({ filters, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [apiToday, setApiToday] = useState(null);
  const [trendDays, setTrendDays] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [quick, setQuick] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [drawerTarget, setDrawerTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = {
        page, limit,
        ...(sortBy && { sort_by: sortBy, sort_dir: sortDir }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.user_id && { user_id: filters.user_id }),
        ...(filters.role && { role: filters.role }),
        ...(filters.team_id && { team_id: filters.team_id }),
        ...(filters.session_status && filters.session_status !== 'all' && { session_status: filters.session_status }),
        ...(quick && { quick }),
      };
      const r = await axios.get(`${API}/api/reports/attendance/daily`, { params });
      setRows(r.data.data || []);
      setSummary(r.data.summary || {});
      setApiToday(r.data.today || null);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      // ATT-UI-SPARK-001 — trend context: when the applied range is a single day, load the trailing 7 ET days
      // from the SAME endpoint/definitions (same dept/user/role scope, no quick filter) purely for sparkline + vs-yesterday.
      const byDay = r.data.summary?.by_day || [];
      if (byDay.length >= 2) {
        setTrendDays(byDay);
      } else {
        const anchor = filters.end_date || filters.start_date || r.data.today;
        if (anchor) {
          const d = new Date(`${anchor}T00:00:00`); d.setDate(d.getDate() - 6);
          const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const tp = { page: 1, limit: 1, start_date: from, end_date: anchor, ...(filters.user_id && { user_id: filters.user_id }), ...(filters.role && { role: filters.role }), ...(filters.team_id && { team_id: filters.team_id }) };
          axios.get(`${API}/api/reports/attendance/daily`, { params: tp }).then(t => setTrendDays(t.data.summary?.by_day || [])).catch(() => setTrendDays([]));
        }
      }
    } catch (e) {
      console.error('daily attendance fetch failed', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortDir, quick, filters, refreshKey]);

  useEffect(() => { setPage(1); }, [filters, quick, limit, sortBy, sortDir]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (field) => {
    if (!field) return;
    if (sortBy === field) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortBy(null); setSortDir('asc'); }
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  return (
    <div className="space-y-4" data-testid="attendance-daily-view">
      {/* KPI cards = quick filters (click toggles the same server-side `quick` filter). Counts are the API summary — no trends invented. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[12px] text-slate-500" data-testid="daily-total-label"><span className="font-semibold text-slate-800">{total}</span> employee-day{total === 1 ? '' : 's'}{quick && <span className="text-slate-400"> · filtered by {QUICK_FILTERS.find(f => f.key === quick)?.label}</span>}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="attendance-kpi-cards">
        {QUICK_FILTERS.map((f, i) => {
          const active = quick === f.key;
          const count = f.countKey ? summary[f.countKey] : null;
          const t = trendFor(f, trendDays, apiToday);
          const delta = f.live
            ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid="kpi-live-chip"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animation: 'liveops-pulse 1.6s infinite' }} />Live</span>
            : t.delta
              ? <AnDelta pct={t.delta.pct} abs={t.delta.abs} invert={f.invert} label="vs yesterday" testId={`kpi-delta-${f.key}`} />
              : <AnDelta pct={null} label={t.series ? 'no prior day in range' : 'no trend data'} testId={`kpi-delta-${f.key}`} />;
          return (
            <div key={f.key} className={`rounded-2xl ${active ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}>
              <AnKpiCard icon={f.icon} tone={f.tone} label={f.label} value={typeof count === 'number' ? count : 0} delay={i * 50} testid={`quick-filter-${f.key}`}
                onClick={() => setQuick(active ? '' : f.key)} loading={loading && count == null} delta={delta} series={t.series} />
            </div>
          );
        })}
      </div>

      {/* Daily summary table */}
      <AnCard className="overflow-hidden" delay={200}>
        {loading ? (
          <AnLoadingState label="Loading attendance…" testId="daily-view-loading" />
        ) : loadError ? (
          <div className="p-3"><AnErrorState title="Could not load attendance data." onRetry={fetchData} testId="daily-view-error" /></div>
        ) : rows.length === 0 ? (
          <div className="p-3"><AnEmptyState tone="neutral" icon={<Clock className="h-5 w-5" />} title="No attendance found" subtitle="Try adjusting the date range or filters." testId="daily-view-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] att-table">
              <thead className="an-thead">
                <tr>
                  {COLUMNS.map(([h, field]) => (
                    <th key={h || '_actions'} className={`text-left ${h === '' ? 'text-right' : ''}`}>
                      {field ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(field)}
                          className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${sortBy === field ? 'underline decoration-white/60 underline-offset-4' : ''}`}
                          data-testid={`daily-sort-${field}`}
                        >
                          {h}
                          {sortBy === field
                            ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                            : <ArrowUpDown className="w-3 h-3 opacity-60" />}
                        </button>
                      ) : (h || 'Actions')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const active = !r.is_complete && r.session_count > 0;
                  const onLeave = (r.status || '').startsWith('On Leave');
                  return (
                    <tr
                      key={`${r.user_id}_${r.work_date}`}
                      className="an-row cursor-pointer"
                      onClick={() => r.session_count > 0 && setDrawerTarget({ user_id: r.user_id, date: r.work_date, user_name: r.user_name })}
                      data-testid={`daily-row-${r.user_id}-${r.work_date}`}
                    >
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          <AnAvatar name={r.user_name} tone={active ? 'emerald' : 'indigo'} />
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-800">{r.user_name}</span>
                              {r.flags?.needs_review && (
                                <AlertTriangle
                                  className="w-3.5 h-3.5 text-amber-500 flex-shrink-0"
                                  data-testid={`needs-review-icon-${r.user_id}-${r.work_date}`}
                                />
                              )}
                            </div>
                            <div className="text-[10.5px] text-slate-400">{r.employee_id !== '--' ? r.employee_id : r.employee_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-slate-600 leading-tight att-role">{fmtRole(r.role)}</td>
                      <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.work_date)}</td>
                      <td className="px-2 py-2 text-slate-500 whitespace-nowrap" title={r.shift_type_label || ''} data-testid={`shift-cell-${r.user_id}-${r.work_date}`}>
                        {r.shift_start ? (
                          <span className="font-mono text-[11.5px]">{r.shift_start_display} – {r.shift_end_display}</span>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-2 font-mono text-slate-700 whitespace-nowrap">{r.first_login !== '--' ? r.first_login.slice(0, 5) : '—'}</td>
                      <td className="px-2 py-2 font-mono whitespace-nowrap">
                        {active ? <span className="text-emerald-600 font-semibold">Active</span> : (
                          r.last_logout !== '--' ? (
                            <>
                              {r.last_logout.slice(0, 5)}
                              {r.first_login !== '--' && r.last_logout < r.first_login && (
                                <span className="ml-0.5 text-[9px] text-slate-400 align-super" title="Logout on the following day">+1d</span>
                              )}
                            </>
                          ) : '—'
                        )}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {r.session_count > 0 ? (
                          <span className={`inline-flex items-center justify-center min-w-[26px] h-6 text-[11px] font-bold px-2 rounded-full ${r.session_count > 1 ? 'bg-indigo-50 text-indigo-700' : 'bg-blue-50 text-blue-700'}`}>
                            {r.session_count}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-2 font-mono text-slate-700 whitespace-nowrap">{r.total_worked_hours_hhmm}</td>
                      <td className="px-2 py-2 font-mono text-slate-500 whitespace-nowrap">{r.break_minutes > 0 ? r.break_hours_hhmm : '—'}</td>
                      <td className="px-2 py-2 font-mono font-semibold text-emerald-700 whitespace-nowrap">{r.net_payable_hours_hhmm}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <AnStatusPill tone={active ? 'green' : onLeave ? 'amber' : 'blue'} dot testId={`daily-status-${r.user_id}-${r.work_date}`}>
                          {active ? 'Active' : onLeave ? r.status : 'Completed'}
                        </AnStatusPill>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        {r.session_count > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="an-btn h-7 px-1.5 rounded-lg text-[11.5px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 whitespace-nowrap"
                            onClick={() => setDrawerTarget({ user_id: r.user_id, date: r.work_date, user_name: r.user_name })}
                            data-testid={`view-history-${r.user_id}-${r.work_date}`}
                          >
                            <History className="w-3.5 h-3.5 mr-1" /> View History
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !loadError && total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100" data-testid="daily-pagination">
            <span className="text-[12px] text-slate-500" data-testid="daily-record-indicator">
              Showing <span className="font-semibold text-slate-700">{(page - 1) * limit + 1}</span>–<span className="font-semibold text-slate-700">{Math.min(page * limit, total)}</span> of <span className="font-semibold text-slate-700">{total}</span> employee-days
            </span>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-[12px] text-slate-500">
                Rows per page:
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-[12px] text-slate-700"
                  data-testid="daily-rows-per-page"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="an-btn h-8 w-8 p-0 rounded-lg" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="daily-prev-page" aria-label="Previous page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {pageNumbers(page, pages).map((n, i) => n === '…' ? (
                  <span key={`e${i}`} className="px-1 text-[12px] text-slate-400">…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`an-btn h-8 min-w-[32px] px-1.5 rounded-lg text-[12px] font-semibold ${
                      n === page ? 'an-btn-primary' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`daily-page-${n}`}
                  >
                    {n}
                  </button>
                ))}
                <Button size="sm" variant="outline" className="an-btn h-8 w-8 p-0 rounded-lg" disabled={page === pages} onClick={() => setPage((p) => p + 1)} data-testid="daily-next-page" aria-label="Next page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </AnCard>

      <AttendanceDayDrawer target={drawerTarget} onClose={() => setDrawerTarget(null)} />
    </div>
  );
}
